import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, CircleSlash2, Eye, FileWarning, LoaderCircle, Pencil, RotateCcw, Trash2, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { BrandInput, CategoryInput, SubcategoryInput, type CreateCategory } from "./classification-inputs";
import { CodeMemorySuggestions } from "./transaction-editor";
import { TransactionDetailPreview } from "./transaction-detail-preview";
import { applyRecipientBrandMappings, brandContext, type BusinessUnit, type RecipientBrandMapping } from "@/lib/business-units";
import type { FinanceCategory } from "@/lib/categories";
import type { CodeCategoryRuleLike } from "@/lib/finance";
import type { FinancialAccount } from "@/lib/profit-loss";
import {
  criticalDataQualityFields,
  parseAmount,
  refreshTransactionDerived,
  type DataQualityField,
  type Direction,
  type Language,
  type Transaction,
} from "@/lib/finance";

type DataQualityReviewProps = {
  caption?: string;
  reviewLocked?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rows: Transaction[];
  language: Language;
  busy: boolean;
  mode: "import" | "manual" | "dashboard";
  onConfirm: (rows: Transaction[]) => void;
  onCancel: () => void;
  units?: BusinessUnit[];
  categories?: FinanceCategory[];
  accounts?: FinancialAccount[];
  onCreateCategory?: CreateCategory;
  history?: Transaction[];
  rules?: CodeCategoryRuleLike[];
  mappings?: RecipientBrandMapping[];
  originalRows?: Transaction[];
  initialExcludedIndexes?: number[];
  onDraftChange?: (rows: Transaction[], excludedIndexes: number[]) => void | Promise<void>;
};

const fieldOrder: DataQualityField[] = [
  "amount", "direction", "transactionDate", "transactionTime", "paymentRef",
  "sourceName", "sourceBank", "beneficiaryName", "beneficiaryBank", "subject",
  "balance",
];

const fieldLabels: Record<DataQualityField, { id: string; en: string }> = {
  amount: { id: "Nominal", en: "Amount" },
  direction: { id: "Debet/Kredit", en: "Debit/Credit" },
  transactionDate: { id: "Tanggal transaksi", en: "Transaction date" },
  transactionTime: { id: "Jam transaksi", en: "Transaction time" },
  paymentRef: { id: "No. referensi", en: "Reference no." },
  sourceName: { id: "Nama pengirim", en: "Sender name" },
  sourceBank: { id: "Bank pengirim", en: "Sender bank" },
  beneficiaryName: { id: "Nama penerima", en: "Recipient name" },
  beneficiaryBank: { id: "Bank penerima", en: "Recipient bank" },
  subject: { id: "Deskripsi mutasi", en: "Statement description" },
  balance: { id: "Saldo riil", en: "Running balance" },
};

function rowKey(row: Transaction, index: number) {
  return `${row.id}:${index}`;
}

function issueFields(row: Transaction) {
  return fieldOrder.filter((field) => row.missingFields.includes(field));
}

function removeConfirmed(row: Transaction, field: DataQualityField) {
  return row.confirmedEmptyFields.filter((value) => value !== field);
}

function changeField(row: Transaction, field: DataQualityField, value: string) {
  let next: Transaction = { ...row, confirmedEmptyFields: removeConfirmed(row, field) };
  if (field === "amount") next.amount = parseAmount(value);
  if (field === "direction") next.direction = value as Direction;
  if (field === "transactionDate") next.date = value;
  if (field === "transactionTime") {
    next.transactionTimestamp = value;
    if (value) next.date = value.slice(0, 10);
  }
  if (field === "paymentRef") next.paymentRef = value;
  if (field === "sourceName") next.sourceName = value;
  if (field === "sourceBank") next.sourceBank = value;
  if (field === "beneficiaryName") next.beneficiaryName = value;
  if (field === "beneficiaryBank") next.beneficiaryBank = value;
  if (field === "subject") next.subject = value;
  if (field === "balance") next.balance = value.trim() ? parseAmount(value) : undefined;
  return refreshTransactionDerived(next);
}

function currentValue(row: Transaction, field: DataQualityField) {
  if (field === "amount") return row.amount ? String(row.amount) : "";
  if (field === "direction") return row.direction;
  if (field === "transactionDate") return row.date;
  if (field === "transactionTime") return row.transactionTimestamp || "";
  if (field === "paymentRef") return row.paymentRef;
  if (field === "sourceName") return row.sourceName || "";
  if (field === "sourceBank") return row.sourceBank || "";
  if (field === "beneficiaryName") return row.beneficiaryName || "";
  if (field === "beneficiaryBank") return row.beneficiaryBank || "";
  if (field === "subject") return row.subject;
  if (field === "balance") return row.balance === undefined ? "" : String(row.balance);
  return "";
}

type EditableImportField =
  | DataQualityField
  | "ftNumber"
  | "sourceAccount"
  | "beneficiaryAccount"
  | "transferType"
  | "accountLabel";

const editableImportFields: EditableImportField[] = [
  "amount", "direction", "transactionDate", "transactionTime", "paymentRef", "ftNumber",
  "sourceName", "sourceBank", "beneficiaryName", "beneficiaryBank",
  "sourceAccount", "beneficiaryAccount", "subject", "transferType", "accountLabel", "balance",
];

const editableImportLabels: Record<EditableImportField, { id: string; en: string }> = {
  ...fieldLabels,
  ftNumber: { id: "FT Number (opsional)", en: "FT Number (optional)" },
  sourceAccount: { id: "Rekening pengirim", en: "Sender account" },
  beneficiaryAccount: { id: "Rekening penerima", en: "Recipient account" },
  transferType: { id: "Jenis transfer", en: "Transfer type" },
  accountLabel: { id: "Nama rekening", en: "Account name" },
};

function editableValue(row: Transaction, field: EditableImportField) {
  if (field === "ftNumber") return row.ftNumber || "";
  if (field in fieldLabels) return currentValue(row, field as DataQualityField);
  if (field === "sourceAccount") return row.sourceAccount;
  if (field === "beneficiaryAccount") return row.beneficiaryAccount;
  if (field === "transferType") return row.transferType;
  return row.accountLabel;
}

function changeEditableField(row: Transaction, field: EditableImportField, value: string) {
  if (field in fieldLabels) return changeField(row, field as DataQualityField, value);
  const next = { ...row };
  if (field === "ftNumber") next.ftNumber = value.trim();
  if (field === "sourceAccount") next.sourceAccount = value;
  if (field === "beneficiaryAccount") next.beneficiaryAccount = value;
  if (field === "transferType") next.transferType = value;
  if (field === "accountLabel") next.accountLabel = value;
  return refreshTransactionDerived(next);
}

function rowWasEdited(row: Transaction, original: Transaction | undefined) {
  if (!original) return false;
  return editableImportFields.some((field) => editableValue(row, field) !== editableValue(original, field))
    || brandContext(row) !== brandContext(original)
    || row.category !== original.category;
}

export function DataQualityReview({
  caption,
  reviewLocked = false,
  open,
  onOpenChange,
  rows,
  language,
  busy: externalBusy,
  mode,
  onConfirm,
  onCancel,
  units,
  categories,
  accounts,
  onCreateCategory,
  history = [],
  rules = [],
  mappings = [],
  originalRows = rows,
  initialExcludedIndexes = [],
  onDraftChange,
}: DataQualityReviewProps) {
  const [draftBusy, setDraftBusy] = useState(false);
  const busy = externalBusy || draftBusy;
  const draftTimeout = useRef<number | undefined>(undefined);
  const [drafts, setDrafts] = useState<Transaction[]>(rows);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());
  const [editingIndexes, setEditingIndexes] = useState<Set<number>>(new Set());
  const [view, setView] = useState<"included" | "excluded" | "all">("included");
  const [detailTransaction, setDetailTransaction] = useState<Transaction | null>(null);
  const reviewSessionKey = rows.map((row, index) => `${row.id}:${row.sourceRowNumber || index + 1}`).join("|");
  const text = language === "id" ? {
    title: mode === "dashboard" ? "Lengkapi data transaksi" : "Periksa data yang tidak tersedia",
    description: mode !== "dashboard"
      ? "Nilai yang tidak ditemukan pada sumber dibiarkan kosong. Isi sekarang, tandai sengaja kosong, atau lanjutkan agar tetap masuk daftar pemeriksaan dashboard."
      : "Lengkapi nilai yang tersedia atau tandai bahwa sumber memang tidak menyediakannya.",
    incomplete: "Perlu diperiksa",
    acknowledged: "Sengaja kosong",
    complete: "Lengkap",
    critical: "Wajib sebelum disimpan",
    leaveEmpty: "Biarkan kosong",
    undoEmpty: "Batalkan kosong",
    leaveOptional: "Tandai semua kolom opsional sebagai sengaja kosong",
    exclude: "Keluarkan baris",
    restore: "Pulihkan baris",
    edit: "Edit lengkap",
    closeEdit: "Tutup edit",
    reset: "Kembalikan ke data asli",
    ready: mode === "import" ? "Siap diproses" : "Siap diimpor",
    edited: "Sudah diedit",
    excluded: "Tidak akan diimpor",
    includedView: "Akan diproses",
    excludedView: "Tidak diimpor",
    allView: "Semua",
    selected: "dipilih",
    excludeSelected: "Jangan impor pilihan",
    restoreSelected: "Pulihkan pilihan",
    clearSelection: "Batal pilih",
    viewDetails: "Lihat detail lengkap",
    importSummary: "baris akan diproses",
    cancel: mode === "import" ? "Batalkan impor" : mode === "manual" ? "Kembali ke formulir" : "Tutup",
    continue: mode === "import" ? "Lanjutkan impor" : mode === "manual" ? "Lanjutkan pemeriksaan duplikasi" : "Simpan perubahan",
    unresolvedCritical: "Nominal dan Debet/Kredit harus ditentukan atau baris dikeluarkan.",
    noValue: "Tidak tersedia",
  } : {
    title: mode === "dashboard" ? "Complete transaction data" : "Review unavailable source data",
    description: mode !== "dashboard"
      ? "Values absent from the source stay empty. Fill them now, acknowledge the blank, or continue so they remain in the dashboard review queue."
      : "Complete available values or confirm that the source does not provide them.",
    incomplete: "Needs review",
    acknowledged: "Intentionally empty",
    complete: "Complete",
    critical: "Required before saving",
    leaveEmpty: "Leave empty",
    undoEmpty: "Undo empty",
    leaveOptional: "Acknowledge every optional blank",
    exclude: "Exclude row",
    restore: "Restore row",
    edit: "Full edit",
    closeEdit: "Close editor",
    reset: "Restore original data",
    ready: mode === "import" ? "Ready to process" : "Ready to import",
    edited: "Edited",
    excluded: "Will not be imported",
    includedView: "Will be processed",
    excludedView: "Excluded",
    allView: "All",
    selected: "selected",
    excludeSelected: "Exclude selected",
    restoreSelected: "Restore selected",
    clearSelection: "Clear selection",
    viewDetails: "View full details",
    importSummary: "rows will be processed",
    cancel: mode === "import" ? "Cancel import" : mode === "manual" ? "Back to form" : "Close",
    continue: mode === "import" ? "Continue import review" : mode === "manual" ? "Continue duplicate review" : "Save changes",
    unresolvedCritical: "Amount and Debit/Credit must be set or the row must be excluded.",
    noValue: "Unavailable",
  };

  const [reviewPage, setReviewPage] = useState(0);
  useEffect(() => {
    if (!open) return;
    setReviewPage(0);
    setDrafts(rows);
    setExcluded(new Set(initialExcludedIndexes.map((index) => rowKey(rows[index], index)).filter(Boolean)));
    setSelectedIndexes(new Set());
    setEditingIndexes(new Set());
    setDetailTransaction(null);
    setView("included");
  // Rehydrate only when the review session itself opens or its source identity
  // changes. Parent renders may recreate rows/index arrays; treating those new
  // references as a session would erase edits that the Owner is still making.
  }, [open, reviewSessionKey]);

  useEffect(() => {
    if (!open || !onDraftChange || reviewLocked) return;
    draftTimeout.current = window.setTimeout(() => {
      const excludedIndexes = drafts.flatMap((row, index) => excluded.has(rowKey(row, index)) ? [index] : []);
      void onDraftChange(drafts, excludedIndexes);
    }, 250);
    return () => window.clearTimeout(draftTimeout.current);
  }, [drafts, excluded, onDraftChange, open, reviewLocked]);

  async function finishReview(action: () => void) {
    if (busy) return;
    window.clearTimeout(draftTimeout.current);
    setDraftBusy(true);
    try {
      if (!reviewLocked) await onDraftChange?.(drafts, drafts.flatMap((row, index) => excluded.has(rowKey(row, index)) ? [index] : []));
    } finally { setDraftBusy(false); }
    action();
  }

  const included = useMemo(() => drafts.filter((row, index) => !excluded.has(rowKey(row, index))), [drafts, excluded]);
  const unresolvedCritical = included.some((row) => criticalDataQualityFields.some((field) => row.missingFields.includes(field)));
  const unresolvedCount = included.reduce((sum, row) => sum + row.missingFields.filter((field) => !row.confirmedEmptyFields.includes(field)).length, 0);
  const editedCount = drafts.reduce((sum, row, index) => sum + (!excluded.has(rowKey(row, index)) && rowWasEdited(row, originalRows[index]) ? 1 : 0), 0);
  const displayRows = drafts.map((row, index) => ({ row, index })).filter(({ row, index }) => view === "all" || (view === "excluded") === excluded.has(rowKey(row, index)));
  const reviewPages = Math.max(1, Math.ceil(displayRows.length / 20));
  const visibleRows = displayRows.slice(reviewPage * 20, (reviewPage + 1) * 20);

  useEffect(() => {
    setReviewPage((current) => Math.min(current, reviewPages - 1));
  }, [reviewPages]);

  const updateRow = (index: number, field: DataQualityField, value: string) => {
    setDrafts((current) => current.map((row, rowIndex) => rowIndex === index ? mode === "import" ? applyRecipientBrandMappings([changeField(row, field, value)], mappings)[0] : changeField(row, field, value) : row));
  };

  const updateEditableRow = (index: number, field: EditableImportField, value: string) => {
    setDrafts((current) => current.map((row, rowIndex) => rowIndex === index
      ? applyRecipientBrandMappings([changeEditableField(row, field, value)], mappings)[0]
      : row));
  };

  const toggleExcluded = (index: number) => {
    const key = rowKey(drafts[index], index);
    setExcluded((current) => { const next = new Set(current); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  };

  const applySelected = (exclude: boolean) => {
    setExcluded((current) => {
      const next = new Set(current);
      for (const index of selectedIndexes) {
        const key = rowKey(drafts[index], index);
        if (exclude) next.add(key); else next.delete(key);
      }
      return next;
    });
    setSelectedIndexes(new Set());
  };

  const resetRow = (index: number) => {
    const original = originalRows[index];
    if (!original) return;
    setDrafts((current) => current.map((row, rowIndex) => rowIndex === index ? original : row));
  };

  const toggleEmpty = (index: number, field: DataQualityField) => {
    if (criticalDataQualityFields.includes(field)) return;
    setDrafts((current) => current.map((row, rowIndex) => {
      if (rowIndex !== index) return row;
      const confirmed = row.confirmedEmptyFields.includes(field)
        ? row.confirmedEmptyFields.filter((value) => value !== field)
        : [...row.confirmedEmptyFields, field];
      return refreshTransactionDerived({ ...row, confirmedEmptyFields: confirmed });
    }));
  };

  const acknowledgeOptional = (index: number) => {
    setDrafts((current) => current.map((row, rowIndex) => {
      if (rowIndex !== index) return row;
      const confirmed = [...new Set([
        ...row.confirmedEmptyFields,
        ...row.missingFields.filter((field) => !criticalDataQualityFields.includes(field)),
      ])];
      return refreshTransactionDerived({ ...row, confirmedEmptyFields: confirmed });
    }));
  };

  return <><Sheet open={open} onOpenChange={(next) => { if (!busy) { if (!next) void finishReview(() => { setDetailTransaction(null); onOpenChange(false); }); else onOpenChange(true); } }}>
    <SheetContent className="w-full overflow-y-auto sm:max-w-5xl">
      <SheetHeader className="border-b px-4 py-5 sm:px-6">
        <SheetTitle className="flex items-center gap-2"><FileWarning className="size-5" />{text.title}</SheetTitle>
        <SheetDescription>{text.description}</SheetDescription>
        {caption && <p className="break-all text-xs font-semibold text-primary">{caption}</p>}
        {draftBusy && <p role="status" className="flex items-center gap-2 text-xs"><LoaderCircle className="size-4 animate-spin" />{language === "id" ? "Menyimpan draft pemeriksaan…" : "Saving review draft…"}</p>}
        {mode === "import" && <p className="text-xs leading-5 text-muted-foreground">{language === "id"
          ? "Duplikat dengan enam data lengkap dan sama dilewati otomatis. Baris lain disimpan baru; tidak ada pemilihan kandidat atau penggabungan."
          : "Duplicates with all six fields present and equal are skipped automatically. Other rows are saved as new; no candidate selection or merging."}</p>}
      </SheetHeader>
      <fieldset disabled={reviewLocked || busy} className="min-w-0 space-y-5 p-4 pb-28 sm:p-6 sm:pb-28">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Summary value={drafts.filter((row, index) => !excluded.has(rowKey(row, index)) && row.dataReviewStatus === "complete" && !rowWasEdited(row, originalRows[index])).length} label={text.ready} tone="success" />
          <Summary value={editedCount} label={text.edited} tone="neutral" />
          <Summary value={included.filter((row) => row.dataReviewStatus === "needs_review").length} label={text.incomplete} tone="warning" />
          <Summary value={excluded.size} label={text.excluded} tone="excluded" />
        </div>

        {mode === "import" && <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-muted/20 p-2">
          {(["included", "excluded", "all"] as const).map((value) => <Button key={value} type="button" size="sm" variant={view === value ? "default" : "ghost"} onClick={() => { setView(value); setReviewPage(0); setSelectedIndexes(new Set()); }}>{value === "included" ? `${text.includedView} (${included.length})` : value === "excluded" ? `${text.excludedView} (${excluded.size})` : `${text.allView} (${drafts.length})`}</Button>)}
          <span className="ml-auto text-xs font-semibold text-muted-foreground">{included.length} {text.importSummary}</span>
        </div>}

        {mode === "import" && selectedIndexes.size > 0 && <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-xl border border-[#a13b6e]/30 bg-background/95 p-3 shadow-sm backdrop-blur"><span className="mr-auto text-sm font-semibold">{selectedIndexes.size} {text.selected}</span><Button type="button" size="sm" variant="outline" onClick={() => applySelected(true)}><Trash2 />{text.excludeSelected}</Button><Button type="button" size="sm" variant="outline" onClick={() => applySelected(false)}><RotateCcw />{text.restoreSelected}</Button><Button type="button" size="icon-sm" variant="ghost" aria-label={text.clearSelection} onClick={() => setSelectedIndexes(new Set())}><X /></Button></div>}

        {visibleRows.map(({ row, index }) => {
          const key = rowKey(row, index);
          const removed = excluded.has(key);
          const fields = issueFields(row);
          const edited = rowWasEdited(row, originalRows[index]);
          const editing = editingIndexes.has(index);
          return <article key={key} className={`overflow-hidden rounded-xl border bg-card ${removed ? "opacity-55" : ""}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/30 px-4 py-3">
              <div className="flex min-w-0 items-start gap-3">{mode === "import" && <input type="checkbox" className="mt-1 size-4 accent-fuchsia-700" disabled={busy} checked={selectedIndexes.has(index)} aria-label={`${text.selected} #${index + 1}`} onChange={() => setSelectedIndexes((current) => { const next = new Set(current); if (next.has(index)) next.delete(index); else next.add(index); return next; })} />}<button type="button" className="group min-w-0 rounded text-left outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => setDetailTransaction(row)} aria-label={`${text.viewDetails}: ${row.subject || `#${index + 1}`}`}><p className="text-sm font-semibold group-hover:text-[#82305b] dark:group-hover:text-[#f1b9d5]">#{index + 1} · {row.subject || text.noValue}</p><p className="mt-1 text-xs text-muted-foreground">{row.date || "—"} · {row.paymentRef || "—"}</p><span className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-[#82305b] dark:text-[#f1b9d5]"><Eye className="size-3.5" />{text.viewDetails}</span></button></div>
              <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
                {removed ? <Badge variant="outline" className="border-red-500/40 text-red-700 dark:text-red-300">{text.excluded}</Badge> : edited ? <Badge className="bg-[#a13b6e]">{text.edited}</Badge> : row.dataReviewStatus === "complete" ? <Badge className="bg-emerald-600">{text.ready}</Badge> : row.dataReviewStatus === "reviewed" ? <Badge variant="secondary">{text.acknowledged}</Badge> : <Badge variant="outline" className="border-amber-500/40 text-amber-700 dark:text-amber-300">{text.incomplete}</Badge>}
                {mode === "import" && !removed && <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditingIndexes((current) => { const next = new Set(current); if (next.has(index)) next.delete(index); else next.add(index); return next; })}><Pencil />{editing ? text.closeEdit : text.edit}</Button>}
                {mode === "import" && <Button size="sm" variant="ghost" disabled={busy} onClick={() => toggleExcluded(index)}>{removed ? <RotateCcw /> : <Trash2 />}{removed ? text.restore : text.exclude}</Button>}
              </div>
            </div>
            {!removed && <div className="space-y-3 p-4">
              {mode === "import" && editing && <section className="rounded border border-[#a13b6e]/25 bg-[#a13b6e]/5 p-3"><div className="mb-3 flex flex-wrap items-center justify-between gap-2"><p className="text-sm font-semibold">{text.edit}</p><Button type="button" size="sm" variant="outline" disabled={!edited} onClick={() => resetRow(index)}><RotateCcw />{text.reset}</Button></div><div className="grid gap-3 sm:grid-cols-2">{editableImportFields.map((field) => <EditableImportInput key={field} field={field} value={editableValue(row, field)} language={language} disabled={busy} onChange={(value) => updateEditableRow(index, field, value)} />)}</div></section>}
              {mode === "import" && units && categories && <section className="space-y-3 rounded border bg-fuchsia-500/5 p-3"><p className="text-sm font-semibold">{language === "id" ? "Brand & kategori (boleh ditentukan nanti)" : "Brand & category (may be assigned later)"}</p><BrandInput value={brandContext(row)} units={units} candidates={row.brandCandidates} language={language} disabled={busy} onChange={(context) => setDrafts((current) => current.map((value, i) => i === index ? { ...value, businessUnits: context === "unassigned" ? [] : context.split("+"), businessUnit: context.includes("+") ? "unassigned" : context, businessUnitMappingSource: "manual" } : value))} /><CategoryInput value={row.category} direction={row.direction} categories={categories} language={language} disabled={busy} onCreate={onCreateCategory} onChange={(category) => setDrafts((current) => current.map((value, i) => i === index ? { ...value, category, subcategory: undefined, categoryAssignmentSource: "manual" } : value))} /><SubcategoryInput value={row.subcategory} parent={row.category} categories={categories} language={language} disabled={busy} onChange={(subcategory) => setDrafts((current) => current.map((value, i) => i === index ? { ...value, subcategory, categoryAssignmentSource: "manual" } : value))} /><CodeMemorySuggestions transaction={row} history={history} rules={rules} mappings={mappings} units={units} categories={categories} language={language} onChoose={(category, context) => setDrafts((current) => current.map((value, i) => i === index ? { ...value, category, subcategory: undefined, categoryAssignmentSource: "manual", businessUnits: context === "unassigned" ? [] : context.split("+"), businessUnit: context.includes("+") ? "unassigned" : context, businessUnitMappingSource: "manual" } : value))} /></section>}
              {fields.length ? fields.map((field) => {
                const critical = criticalDataQualityFields.includes(field);
                const acknowledged = row.confirmedEmptyFields.includes(field);
                return <div key={field} className={`rounded border p-3 ${acknowledged ? "bg-muted/30" : critical ? "border-red-500/30 bg-red-500/5" : "border-amber-500/25 bg-amber-500/5"}`}>
                  <div className="mb-2 flex items-center justify-between gap-2"><p className="text-xs font-bold uppercase tracking-wide">{fieldLabels[field][language]}</p>{critical && <Badge variant="destructive" className="text-[10px]">{text.critical}</Badge>}</div>
                  <QualityInput field={field} row={row} value={currentValue(row, field)} language={language} disabled={busy || acknowledged} onChange={(value) => updateRow(index, field, value)} />
                  {!critical && <Button type="button" size="sm" variant="ghost" className="mt-2" onClick={() => toggleEmpty(index, field)}>{acknowledged ? <RotateCcw /> : <CircleSlash2 />}{acknowledged ? text.undoEmpty : text.leaveEmpty}</Button>}
                </div>;
              }) : <p className="flex items-center gap-2 py-2 text-sm text-emerald-700 dark:text-emerald-300"><CheckCircle2 className="size-4" />{text.complete}</p>}
              {fields.some((field) => !criticalDataQualityFields.includes(field) && !row.confirmedEmptyFields.includes(field)) && <Button type="button" variant="outline" size="sm" onClick={() => acknowledgeOptional(index)}><CircleSlash2 />{text.leaveOptional}</Button>}
            </div>}
          </article>;
        })}
      </fieldset>
      {displayRows.length > 20 && <div className="flex items-center justify-between gap-2 border-t px-4 py-3"><Button variant="outline" size="sm" disabled={reviewPage === 0 || busy} onClick={() => setReviewPage((page) => page - 1)}>{language === "id" ? "Sebelumnya" : "Previous"}</Button><span className="text-xs">{reviewPage + 1} / {reviewPages}</span><Button variant="outline" size="sm" disabled={reviewPage + 1 >= reviewPages || busy} onClick={() => setReviewPage((page) => page + 1)}>{language === "id" ? "Berikutnya" : "Next"}</Button></div>}
      <div className="sticky bottom-0 border-t bg-background/95 p-4 backdrop-blur sm:px-6">
        {reviewLocked && <p role="status" className="mb-3 text-xs leading-5 text-amber-800 dark:text-amber-200">{language === "id"
          ? "Data percobaan simpan dikunci agar retry tetap sama. Periksa hasil dahulu; koreksi berikutnya lewat edit transaksi."
          : "The submitted review is locked so retries stay identical. Verify the result first; make further changes through transaction editing."}</p>}
        {unresolvedCritical && <p role="alert" className="mb-3 flex items-center gap-2 text-sm text-red-600 dark:text-red-300"><AlertTriangle className="size-4" />{text.unresolvedCritical}</p>}
        <div className="grid gap-2 sm:grid-cols-[1fr_2fr]"><Button variant="outline" disabled={busy} onClick={() => void finishReview(onCancel)}>{reviewLocked ? (language === "id" ? "Jeda pemeriksaan" : "Pause review") : text.cancel}</Button><Button disabled={busy || unresolvedCritical || !included.length} onClick={() => void finishReview(() => onConfirm(included))}>{busy ? <LoaderCircle className="animate-spin" /> : <CheckCircle2 />}{reviewLocked ? (language === "id" ? "Periksa / ulangi penyimpanan" : "Verify / retry save") : text.continue}{unresolvedCount ? ` · ${unresolvedCount}` : ""}</Button></div>
      </div>
    </SheetContent>
  </Sheet><TransactionDetailPreview transaction={detailTransaction} onOpenChange={(next) => { if (!next) setDetailTransaction(null); }} language={language} units={units} categories={categories} accounts={accounts} persisted={mode === "dashboard"} /></>;
}

function QualityInput({ field, value, language, disabled, onChange }: { field: DataQualityField; row: Transaction; value: string; language: Language; disabled: boolean; onChange: (value: string) => void }) {
  if (field === "direction") return <Select value={value || "empty"} disabled={disabled} onValueChange={(next) => onChange(next === "empty" ? "" : next)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="empty" disabled>{language === "id" ? "Pilih tipe" : "Select type"}</SelectItem><SelectItem value="CREDIT">{language === "id" ? "Kredit / pemasukan" : "Credit / income"}</SelectItem><SelectItem value="DEBIT">{language === "id" ? "Debet / pengeluaran" : "Debit / expense"}</SelectItem></SelectContent></Select>;
  if (field === "subject") return <textarea value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} rows={2} className="min-h-20 w-full resize-y rounded border bg-background px-3 py-2 text-sm disabled:opacity-60" />;
  const type = field === "transactionDate" ? "date" : field === "transactionTime" ? "datetime-local" : field === "amount" || field === "balance" ? "number" : "text";
  return <Input type={type} value={value} disabled={disabled} inputMode={type === "number" ? "decimal" : undefined} onChange={(event) => onChange(event.target.value)} min={field === "amount" ? "1" : undefined} placeholder={fieldLabels[field][language]} />;
}

function EditableImportInput({ field, value, language, disabled, onChange }: { field: EditableImportField; value: string; language: Language; disabled: boolean; onChange: (value: string) => void }) {
  const label = editableImportLabels[field][language];
  if (field === "direction") return <label className="block"><span className="mb-1.5 block text-xs font-semibold text-muted-foreground">{label}</span><Select value={value || "empty"} disabled={disabled} onValueChange={(next) => onChange(next === "empty" ? "" : next)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="empty" disabled>{language === "id" ? "Pilih tipe" : "Select type"}</SelectItem><SelectItem value="CREDIT">{language === "id" ? "Kredit / pemasukan" : "Credit / income"}</SelectItem><SelectItem value="DEBIT">{language === "id" ? "Debet / pengeluaran" : "Debit / expense"}</SelectItem></SelectContent></Select></label>;
  if (field === "subject") return <label className="block sm:col-span-2"><span className="mb-1.5 block text-xs font-semibold text-muted-foreground">{label}</span><textarea value={value} disabled={disabled} rows={3} className="min-h-24 w-full resize-y rounded border bg-background px-3 py-2 text-sm" onChange={(event) => onChange(event.target.value)} /></label>;
  const type = field === "transactionDate" ? "date" : field === "transactionTime" ? "datetime-local" : field === "amount" || field === "balance" ? "number" : "text";
  return <label className="block"><span className="mb-1.5 block text-xs font-semibold text-muted-foreground">{label}</span><Input type={type} value={value} disabled={disabled} inputMode={type === "number" ? "decimal" : undefined} min={field === "amount" ? "1" : undefined} onChange={(event) => onChange(event.target.value)} /></label>;
}

function Summary({ value, label, tone }: { value: number; label: string; tone: "warning" | "neutral" | "success" | "excluded" }) {
  const style = tone === "warning" ? "border-amber-500/30 bg-amber-500/8" : tone === "success" ? "border-emerald-500/30 bg-emerald-500/8" : tone === "excluded" ? "border-red-500/30 bg-red-500/8" : "bg-muted/30";
  return <div className={`rounded-xl border p-4 ${style}`}><p className="text-2xl font-semibold tabular-nums">{value}</p><p className="mt-1 text-sm text-muted-foreground">{label}</p></div>;
}
